// MGRS / UTM グリッド生成の共有ロジック
//   - ライブ地図(MapManager)と印刷(PrintMapPopup)の両方から使う純粋関数群
//   - WGS84 横メルカトル(UTM)の forward/inverse を自前実装（依存追加なし）
//   - generateGridGeoJSON() がグリッド線とラベルを GeoJSON Feature で返す
//
// 参考: UTM 変換式は USGS の標準横メルカトル式、100km 正方形 ID は
//       MGRS(AA スキーム / WGS84)の標準アルゴリズムを移植したもの。

const A_WGS84 = 6378137.0;            // 長半径
const F_WGS84 = 1 / 298.257223563;    // 扁平率
const K0 = 0.9996;                    // UTM 縮尺係数
const E2 = F_WGS84 * (2 - F_WGS84);   // 第一離心率^2
const EP2 = E2 / (1 - E2);            // 第二離心率^2

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

// 経度からUTMゾーン番号(1-60)
export function utmZoneFromLng(lng) {
	return Math.floor(((lng + 180) % 360) / 6) + 1;
}

// ゾーン中央子午線(度)
function centralMeridian(zone) {
	return (zone - 1) * 6 - 180 + 3;
}

// 緯度バンド字(C-X、I/O除外)。-80〜84度。
export function latBand(lat) {
	if (lat < -80) return 'C';
	if (lat > 84) return 'X';
	const bands = 'CDEFGHJKLMNPQRSTUVWX';
	const idx = Math.min(bands.length - 1, Math.floor((lat + 80) / 8));
	return bands.charAt(idx);
}

// 緯度経度 → UTM。forceZone を渡すとそのゾーンの子午線で投影する。
export function llToUtm(lat, lng, forceZone) {
	const zone = forceZone || utmZoneFromLng(lng);
	const lonOrigin = centralMeridian(zone);
	const latRad = lat * DEG;
	const dLonRad = (lng - lonOrigin) * DEG;

	const N = A_WGS84 / Math.sqrt(1 - E2 * Math.sin(latRad) ** 2);
	const T = Math.tan(latRad) ** 2;
	const C = EP2 * Math.cos(latRad) ** 2;
	const Acoef = Math.cos(latRad) * dLonRad;

	const M = A_WGS84 * (
		(1 - E2 / 4 - 3 * E2 ** 2 / 64 - 5 * E2 ** 3 / 256) * latRad
		- (3 * E2 / 8 + 3 * E2 ** 2 / 32 + 45 * E2 ** 3 / 1024) * Math.sin(2 * latRad)
		+ (15 * E2 ** 2 / 256 + 45 * E2 ** 3 / 1024) * Math.sin(4 * latRad)
		- (35 * E2 ** 3 / 3072) * Math.sin(6 * latRad)
	);

	const easting = K0 * N * (
		Acoef + (1 - T + C) * Acoef ** 3 / 6
		+ (5 - 18 * T + T ** 2 + 72 * C - 58 * EP2) * Acoef ** 5 / 120
	) + 500000.0;

	let northing = K0 * (M + N * Math.tan(latRad) * (
		Acoef ** 2 / 2
		+ (5 - T + 9 * C + 4 * C ** 2) * Acoef ** 4 / 24
		+ (61 - 58 * T + T ** 2 + 600 * C - 330 * EP2) * Acoef ** 6 / 720
	));

	const hemisphere = lat >= 0 ? 'N' : 'S';
	if (lat < 0) northing += 10000000.0;

	return { zone, hemisphere, easting, northing };
}

// UTM → 緯度経度
export function utmToLl(zone, hemisphere, easting, northing) {
	const e1 = (1 - Math.sqrt(1 - E2)) / (1 + Math.sqrt(1 - E2));
	const x = easting - 500000.0;
	let y = northing;
	if (hemisphere === 'S') y -= 10000000.0;

	const lonOrigin = centralMeridian(zone);
	const M = y / K0;
	const mu = M / (A_WGS84 * (1 - E2 / 4 - 3 * E2 ** 2 / 64 - 5 * E2 ** 3 / 256));

	const phi1 = mu
		+ (3 * e1 / 2 - 27 * e1 ** 3 / 32) * Math.sin(2 * mu)
		+ (21 * e1 ** 2 / 16 - 55 * e1 ** 4 / 32) * Math.sin(4 * mu)
		+ (151 * e1 ** 3 / 96) * Math.sin(6 * mu)
		+ (1097 * e1 ** 4 / 512) * Math.sin(8 * mu);

	const N1 = A_WGS84 / Math.sqrt(1 - E2 * Math.sin(phi1) ** 2);
	const T1 = Math.tan(phi1) ** 2;
	const C1 = EP2 * Math.cos(phi1) ** 2;
	const R1 = A_WGS84 * (1 - E2) / Math.pow(1 - E2 * Math.sin(phi1) ** 2, 1.5);
	const D = x / (N1 * K0);

	const lat = phi1 - (N1 * Math.tan(phi1) / R1) * (
		D ** 2 / 2
		- (5 + 3 * T1 + 10 * C1 - 4 * C1 ** 2 - 9 * EP2) * D ** 4 / 24
		+ (61 + 90 * T1 + 298 * C1 + 45 * T1 ** 2 - 252 * EP2 - 3 * C1 ** 2) * D ** 6 / 720
	);
	const lng = lonOrigin * DEG + (
		D - (1 + 2 * T1 + C1) * D ** 3 / 6
		+ (5 - 2 * C1 + 28 * T1 - 3 * C1 ** 2 + 8 * EP2 + 24 * T1 ** 2) * D ** 5 / 120
	) / Math.cos(phi1);

	return { lat: lat * RAD, lng: lng * RAD };
}

// --- MGRS 100km 正方形 ID（標準 AA スキーム） ---
const A_CODE = 65, I_CODE = 73, O_CODE = 79, V_CODE = 86, Z_CODE = 90;
const NUM_100K_SETS = 6;
const SET_ORIGIN_COLUMN_LETTERS = 'AJSAJS';
const SET_ORIGIN_ROW_LETTERS = 'AFAFAF';

function get100kSetForZone(zone) {
	let setParm = zone % NUM_100K_SETS;
	if (setParm === 0) setParm = NUM_100K_SETS;
	return setParm;
}

function getLetter100kID(column, row, parm) {
	const index = parm - 1;
	const colOrigin = SET_ORIGIN_COLUMN_LETTERS.charCodeAt(index);
	const rowOrigin = SET_ORIGIN_ROW_LETTERS.charCodeAt(index);

	let colInt = colOrigin + column - 1;
	let rowInt = rowOrigin + row;
	let rollover = false;

	if (colInt > Z_CODE) { colInt = colInt - Z_CODE + A_CODE - 1; rollover = true; }
	if (colInt === I_CODE || (colOrigin < I_CODE && colInt > I_CODE) || ((colInt > I_CODE || colOrigin < I_CODE) && rollover)) colInt++;
	if (colInt === O_CODE || (colOrigin < O_CODE && colInt > O_CODE) || ((colInt > O_CODE || colOrigin < O_CODE) && rollover)) {
		colInt++;
		if (colInt === I_CODE) colInt++;
	}
	if (colInt > Z_CODE) colInt = colInt - Z_CODE + A_CODE - 1;

	rollover = false;
	if (rowInt > V_CODE) { rowInt = rowInt - V_CODE + A_CODE - 1; rollover = true; }
	if (rowInt === I_CODE || (rowOrigin < I_CODE && rowInt > I_CODE) || ((rowInt > I_CODE || rowOrigin < I_CODE) && rollover)) rowInt++;
	if (rowInt === O_CODE || (rowOrigin < O_CODE && rowInt > O_CODE) || ((rowInt > O_CODE || rowOrigin < O_CODE) && rollover)) {
		rowInt++;
		if (rowInt === I_CODE) rowInt++;
	}
	if (rowInt > V_CODE) rowInt = rowInt - V_CODE + A_CODE - 1;

	return String.fromCharCode(colInt) + String.fromCharCode(rowInt);
}

// 100km 正方形 ID 文字列（例: "54SUE"）を緯度経度から取得
export function mgrs100kId(lat, lng) {
	const { zone, easting, northing } = llToUtm(lat, lng);
	const setParm = get100kSetForZone(zone);
	const setColumn = Math.floor(easting / 100000);
	const setRow = Math.floor(northing / 100000) % 20;
	return `${zone}${latBand(lat)}${getLetter100kID(setColumn, setRow, setParm)}`;
}

// 実効グリッド間隔(m)を選ぶ。画面ピクセル幅も考慮し、画面上の線本数が
// おおむね 6〜13 本（≒1セル ~150〜300px）に収まるようにする。
//   1. 目標本数 = clamp(viewportPx / 200, 6, 13)（PCの横長画面では本数多め）
//   2. 目標間隔 = 画面幅(実距離) / 目標本数
//   3. それに最も近い 1-2-5 系列(10m〜100km)の値を採用
// これにより、画面が広い(PC)ほどセルが大きくなりすぎず、狭い(スマホ)ほど密になりすぎない。
export function pickEffectiveStep(widthMeters, viewportPx = 1000) {
	const targetLines = Math.max(6, Math.min(13, viewportPx / 200));
	const desired = widthMeters / targetLines;
	let best = 0;
	let bestErr = Infinity;
	for (let e = 1; e <= 5; e++) {        // 10m 〜 100km
		for (const m of [1, 2, 5]) {
			const v = m * Math.pow(10, e);
			if (v > 100000) continue;
			const err = Math.abs(Math.log(v / Math.max(1e-6, desired)));
			if (err < bestErr) { bestErr = err; best = v; }
		}
	}
	return best;
}

// 簡易ハバーサイン（東西方向の距離概算に使用）
function haversine(lat1, lng1, lat2, lng2) {
	const R = 6371000;
	const dLat = (lat2 - lat1) * DEG;
	const dLng = (lng2 - lng1) * DEG;
	const a = Math.sin(dLat / 2) ** 2
		+ Math.cos(lat1 * DEG) * Math.cos(lat2 * DEG) * Math.sin(dLng / 2) ** 2;
	return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// グリッド線ラベルの主要桁。
//   100km 正方形内の座標をグリッド間隔で割った値を、間隔に応じた桁数で表示する。
//   1km 間隔→2桁(例 74)、100m→3桁(例 974)、10m→4桁。ズームで桁が増える。
function principalLabel(value, interval) {
	const within = ((value % 100000) + 100000) % 100000; // 100km 正方形内の座標(m)
	const digits = Math.max(1, Math.round(Math.log10(100000 / interval)));
	const idx = Math.floor(within / interval);
	return String(idx).padStart(digits, '0');
}

// 表示範囲(bounds)から MGRS/UTM グリッドの GeoJSON を生成。
//   bounds: { north, south, east, west }
//   mode:   'mgrs' | 'utm'
//   opts:   { color, haloColor, width, opacity, viewportPx }
//
// 設計:
//   1. 画面幅(実距離)＋画面ピクセル幅から実効間隔 step を決定（画面で約 6〜13 本）
//   2. 主線(step より一段粗い 10 進グリッド interval の倍数)を太く、補助線(step)を細く描く
//   3. 座標数値は step の分解能で表示（補助線にも数字、ズームで桁が増える）
//   4. フォントサイズは実効線数連動（疎→大, 密→小）
//   5. ゾーン表記(MGRS:100km ID / UTM:ゾーン)は主セルの左下隅。強ズーム時はセル内 5 箇所
// 戻り値: { lines, labels, interval, step, numberOfGrids }
export function generateGridGeoJSON(bounds, mode, opts = {}) {
	const { north, south, east, west } = bounds;
	if (!(north > south) || !(east > west)) return { lines: [], labels: [], interval: 0 };

	const centerLat = (north + south) / 2;
	const centerLng = (east + west) / 2;
	const widthMeters = haversine(centerLat, west, centerLat, east);

	const step = opts.interval || pickEffectiveStep(widthMeters, opts.viewportPx);
	if (!step) return { lines: [], labels: [], interval: 0, step: 0, numberOfGrids: 0 };

	// 座標数値の表示単位（step 以下で最大の 10 のべき乗）。補助線も区別できる桁数にする。
	const labelUnit = Math.pow(10, Math.floor(Math.log10(step) + 1e-9));
	// 主線 = 一段粗い 10 進グリッド。step が 10 進なら 10×, 2-5 系列でも 10×labelUnit を主とする。
	const interval = labelUnit * 10;
	const numberOfGrids = widthMeters / interval;       // ゾーン表記の配置判定に使用
	const effLines = widthMeters / step;                // 画面内の実効線数

	const color = opts.color || '#dd5144';
	const haloColor = opts.haloColor || '#ffffff';
	const baseWidth = opts.width != null ? opts.width : 1;
	const baseOpacity = opts.opacity != null ? opts.opacity : 0.7;

	// フォントサイズ: 実効線数が多いほど小さく（11〜20px にクランプ）
	const fontMain = Math.max(11, Math.min(20, 22 - effLines * 0.8));
	const fontSub = Math.max(10, fontMain * 0.82);
	const fontSquare = Math.min(20, fontMain * 1.05);

	// 主線判定: interval(10×labelUnit) の倍数が主線、それ以外(step のみ)は補助線
	const isMain = (v) => Math.abs(v / interval - Math.round(v / interval)) < 1e-6;

	// 主線=設定の太さ、補助線=その半分
	const mainWidth = baseWidth;
	const subWidth = Math.max(0.25, baseWidth * 0.5);
	const mainOpacity = baseOpacity;
	const subOpacity = Math.max(0.2, baseOpacity * 0.5);

	const zone = utmZoneFromLng(centerLng);
	const hemisphere = centerLat >= 0 ? 'N' : 'S';

	// 4隅を固定ゾーンで投影し、E/N の範囲を求める
	const corners = [
		llToUtm(north, west, zone), llToUtm(north, east, zone),
		llToUtm(south, west, zone), llToUtm(south, east, zone),
	];
	const minE = Math.min(...corners.map(c => c.easting));
	const maxE = Math.max(...corners.map(c => c.easting));
	const minN = Math.min(...corners.map(c => c.northing));
	const maxN = Math.max(...corners.map(c => c.northing));

	// 実効間隔 step で線を生成
	const eastStart = Math.floor(minE / step) * step;
	const eastEnd = Math.ceil(maxE / step) * step;
	const northStart = Math.floor(minN / step) * step;
	const northEnd = Math.ceil(maxN / step) * step;

	const eastLines = [];
	for (let e = eastStart; e <= eastEnd + 1e-6; e += step) eastLines.push(e);
	const northLines = [];
	for (let n = northStart; n <= northEnd + 1e-6; n += step) northLines.push(n);

	// 過密ガード（極端なズーム/範囲での暴走防止）
	if (eastLines.length > 80 || northLines.length > 80) {
		return { lines: [], labels: [], interval, step, numberOfGrids };
	}

	const SAMPLES = 12; // 1本のグリッド線を構成するサンプル点数（緯度経度上で湾曲するため）
	const lines = [];
	const labels = [];

	const lineFeature = (coords, main) => ({
		type: 'Feature',
		geometry: { type: 'LineString', coordinates: coords },
		properties: { color, width: main ? mainWidth : subWidth, opacity: main ? mainOpacity : subOpacity },
	});

	// 縦線（イースティング一定）
	for (const e of eastLines) {
		const coords = [];
		for (let s = 0; s <= SAMPLES; s++) {
			const n = minN + (maxN - minN) * s / SAMPLES;
			const { lat, lng } = utmToLl(zone, hemisphere, e, n);
			coords.push([lng, lat]);
		}
		lines.push(lineFeature(coords, isMain(e)));
	}
	// 横線（ノージング一定）
	for (const n of northLines) {
		const coords = [];
		for (let s = 0; s <= SAMPLES; s++) {
			const e = minE + (maxE - minE) * s / SAMPLES;
			const { lat, lng } = utmToLl(zone, hemisphere, e, n);
			coords.push([lng, lat]);
		}
		lines.push(lineFeature(coords, isMain(n)));
	}

	const inBounds = (lng, lat) => lng >= west && lng <= east && lat >= south && lat <= north;

	// 座標番号: 線の上・セル区間の中間に配置。各線で約4箇所に間引いて反復（密集防止）。
	// 主線は大きめ、補助線は小さめのフォント。
	const strideY = Math.max(1, Math.round((northLines.length - 1) / 4));
	const strideX = Math.max(1, Math.round((eastLines.length - 1) / 4));

	for (const e of eastLines) {
		const main = isMain(e);
		const label = principalLabel(e, labelUnit);
		const fs = main ? fontMain : fontSub;
		for (let j = 0; j + 1 < northLines.length; j += strideY) {
			const n = (northLines[j] + northLines[j + 1]) / 2;
			const { lat, lng } = utmToLl(zone, hemisphere, e, n);
			if (!inBounds(lng, lat)) continue;
			labels.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties: { gridLabel: true, label, color, haloColor, fontSize: fs } });
		}
	}
	for (const n of northLines) {
		const main = isMain(n);
		const label = principalLabel(n, labelUnit);
		const fs = main ? fontMain : fontSub;
		for (let i = 0; i + 1 < eastLines.length; i += strideX) {
			const e = (eastLines[i] + eastLines[i + 1]) / 2;
			const { lat, lng } = utmToLl(zone, hemisphere, e, n);
			if (!inBounds(lng, lat)) continue;
			labels.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties: { gridLabel: true, label, color, haloColor, fontSize: fs } });
		}
	}

	// ゾーン表記を「主セル」（interval 区切りのマス）に配置。
	//   MGRS: 100km 正方形 ID（例 54SUE） / UTM: グリッドゾーン（例 53S）
	{
		const mainEast = [];
		for (let e = Math.floor(minE / interval) * interval; e <= Math.ceil(maxE / interval) * interval + 1e-6; e += interval) mainEast.push(e);
		const mainNorth = [];
		for (let n = Math.floor(minN / interval) * interval; n <= Math.ceil(maxN / interval) * interval + 1e-6; n += interval) mainNorth.push(n);

		const zoneLabelOf = (lat, lng) => (mode === 'mgrs')
			? mgrs100kId(lat, lng)
			: `${utmZoneFromLng(lng)}${latBand(lat)}`;

		const pushSquare = (e, n) => {
			const { lat, lng } = utmToLl(zone, hemisphere, e, n);
			if (!inBounds(lng, lat)) return;
			labels.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties: { gridSquareLabel: true, label: zoneLabelOf(lat, lng), color, haloColor, fontSize: fontSquare } });
		};

		for (let i = 0; i + 1 < mainEast.length; i++) {
			for (let j = 0; j + 1 < mainNorth.length; j++) {
				const e0 = mainEast[i], e1 = mainEast[i + 1];
				const n0 = mainNorth[j], n1 = mainNorth[j + 1];
				if (numberOfGrids > 0.7) {
					// 通常: 主セルの左下隅の内側に 1 個
					pushSquare(e0 + (e1 - e0) * 0.08, n0 + (n1 - n0) * 0.08);
				} else {
					// 強ズーム: 主線がほぼ見えないので中心＋四隅寄り 5 箇所
					pushSquare((e0 + e1) / 2, (n0 + n1) / 2);
					pushSquare(e0 + (e1 - e0) * 0.2, n0 + (n1 - n0) * 0.2);
					pushSquare(e0 + (e1 - e0) * 0.8, n0 + (n1 - n0) * 0.2);
					pushSquare(e0 + (e1 - e0) * 0.2, n0 + (n1 - n0) * 0.8);
					pushSquare(e0 + (e1 - e0) * 0.8, n0 + (n1 - n0) * 0.8);
				}
			}
		}
	}

	return { lines, labels, interval, step, numberOfGrids };
}
